import api from './api.service';
import type { ApiResponse } from '@/types';

function unwrap<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') return undefined;
  const r = res as ApiResponse<T>;
  if (r.data !== undefined) return r.data as T;
  return res as T;
}

export type StandupMeetingStatus = 'Open' | 'InProgress' | 'Finalized';
export type StandupAttendance = 'Present' | 'Absent' | 'Delegated';

export interface StandupQuestion {
  key: string;
  label: string;
}

export interface StandupBriefCounts {
  completed_follow_ups: number;
  activities_yesterday: number;
  new_leads: number;
  stage_moves: number;
  quotations: number;
  commitments_completed: number;
  commitments_open: number;
  commitments_overdue: number;
  today_calendar: number;
  expected_closures: number;
}

export interface StandupBrief {
  user_id: string;
  as_of_date: string;
  yesterday_date: string;
  completed_follow_ups: Array<Record<string, unknown>>;
  activities_yesterday: Array<Record<string, unknown>>;
  new_leads: Array<Record<string, unknown>>;
  opportunity_stage_moves: Array<Record<string, unknown>>;
  quotations_touched: Array<Record<string, unknown>>;
  commitments_completed: Array<Record<string, unknown>>;
  commitments_open: Array<Record<string, unknown>>;
  commitments_overdue: Array<Record<string, unknown>>;
  today_calendar: Array<Record<string, unknown>>;
  expected_closures: Array<Record<string, unknown>>;
  counts: StandupBriefCounts;
}

export interface StandupContinuity {
  prior_meeting_date: string | null;
  prior_finalized: boolean;
  completed_since_yesterday: string[];
  still_open: string[];
  overdue: string[];
  postponed: Array<{
    id: string;
    postpone_count: number;
    original_follow_up_date?: string;
    follow_up_date?: string;
    commitment_text?: string;
  }>;
  counts: { completed: number; still_open: number; overdue: number; postponed: number };
}

export interface StandupMeeting {
  id: string;
  company_id: string;
  meeting_date: string;
  status: StandupMeetingStatus;
  summary_text?: string | null;
  finalized_at?: string | null;
  finalized_by?: string | null;
}

export interface StandupParticipant {
  id: string;
  user_id: string;
  attendance: StandupAttendance;
  submitted_at?: string | null;
  brief_json?: Record<string, unknown> | null;
}

export interface StandupAnswer {
  id: string;
  participant_id: string;
  question_key: string;
  answer_text?: string | null;
  linked_activity_ids?: string[] | null;
  linked_opportunity_ids?: string[] | null;
}

export interface StandupDecision {
  id: string;
  decision_text: string;
  owner_user_id?: string | null;
  due_date?: string | null;
  status: string;
  crm_activity_id?: string | null;
}

export interface StandupMeetingDetail {
  meeting: StandupMeeting;
  participants: StandupParticipant[];
  answers: StandupAnswer[];
  decisions: StandupDecision[];
  snapshot?: { id: string; payload_json: Record<string, unknown> } | null;
  question_labels?: Record<string, string>;
}

export interface StandupMyBriefResponse {
  meeting: StandupMeeting;
  participant: StandupParticipant;
  brief: StandupBrief;
  continuity: StandupContinuity;
  answers: StandupAnswer[];
  questions: StandupQuestion[];
}

export interface StandupTeamRow {
  user_id: string;
  participant_id: string;
  attendance: StandupAttendance;
  submitted_at?: string | null;
  counts: StandupBriefCounts;
  continuity: StandupContinuity;
}

export interface StandupTeamOverview {
  meeting: StandupMeeting;
  participants: StandupTeamRow[];
  questions: StandupQuestion[];
}

const crmStandupService = {
  async myBrief(date?: string): Promise<StandupMyBriefResponse> {
    const res = await api.get('/crm/standup/me', { params: date ? { date } : undefined });
    return unwrap<StandupMyBriefResponse>(res) as StandupMyBriefResponse;
  },

  async teamOverview(date?: string): Promise<StandupTeamOverview> {
    const res = await api.get('/crm/standup/team', { params: date ? { date } : undefined });
    return unwrap<StandupTeamOverview>(res) as StandupTeamOverview;
  },

  async listMeetings(opts?: { limit?: number; status?: string }): Promise<StandupMeeting[]> {
    const res = await api.get('/crm/standup/meetings', { params: opts });
    return (unwrap<StandupMeeting[]>(res) as StandupMeeting[]) || [];
  },

  async getMeeting(id: string): Promise<StandupMeetingDetail> {
    const res = await api.get(`/crm/standup/meetings/${id}`);
    return unwrap<StandupMeetingDetail>(res) as StandupMeetingDetail;
  },

  async submitAnswers(
    meetingId: string,
    answers: Array<{
      question_key: string;
      answer_text?: string | null;
      linked_activity_ids?: string[];
      linked_opportunity_ids?: string[];
    }>
  ): Promise<StandupMeetingDetail> {
    const res = await api.post(`/crm/standup/meetings/${meetingId}/answers`, { answers });
    return unwrap<StandupMeetingDetail>(res) as StandupMeetingDetail;
  },

  async setAttendance(
    meetingId: string,
    updates: Array<{ user_id: string; attendance: StandupAttendance; delegate_user_id?: string | null }>
  ): Promise<StandupMeetingDetail> {
    const res = await api.post(`/crm/standup/meetings/${meetingId}/attendance`, { updates });
    return unwrap<StandupMeetingDetail>(res) as StandupMeetingDetail;
  },

  async addDecision(
    meetingId: string,
    body: {
      decision_text: string;
      owner_user_id?: string | null;
      due_date?: string | null;
      create_follow_up?: boolean;
    }
  ): Promise<StandupMeetingDetail> {
    const res = await api.post(`/crm/standup/meetings/${meetingId}/decisions`, body);
    return unwrap<StandupMeetingDetail>(res) as StandupMeetingDetail;
  },

  async finalize(meetingId: string): Promise<StandupMeetingDetail> {
    const res = await api.post(`/crm/standup/meetings/${meetingId}/finalize`);
    return unwrap<StandupMeetingDetail>(res) as StandupMeetingDetail;
  },

  async spiMe(userId?: string): Promise<Record<string, unknown> | undefined> {
    const res = await api.get('/sales/sales-performance/me', {
      params: userId ? { userId } : undefined,
    });
    return unwrap<Record<string, unknown>>(res);
  },

  async spiTeam(): Promise<unknown> {
    const res = await api.get('/sales/sales-performance/team');
    return unwrap(res);
  },
};

export default crmStandupService;
